import { AI_VISIBILITY_MEASUREMENT_METHOD } from "@/lib/aiVisibilityBenchmark";

export const SITE_ENTITY_IDS = {
  organization: "https://integrated.social/#organization",
  website: "https://integrated.social/#website",
  homepage: "https://integrated.social/#webpage",
  founder: "https://integrated.social/modi-elnadi/#person",
} as const;

const baseUrl = "https://integrated.social/";

const homepageCoreServiceOffers = [
  {
    name: "SEO, AEO and GEO programme for B2B",
    id: "https://integrated.social/services/seo-aeo-geo/#service",
    url: "https://integrated.social/services/seo-aeo-geo/",
  },
  {
    name: "AI Citation Service for ChatGPT and Gemini",
    id: "https://integrated.social/services/ai-citation-service/#service",
    url: "https://integrated.social/services/ai-citation-service/",
  },
  {
    name: "Full AI Growth Engine",
    id: "https://integrated.social/services/full-ai-growth-engine/#service",
    url: "https://integrated.social/services/full-ai-growth-engine/",
  },
  {
    name: "AI Marketing Strategy UK",
    id: "https://integrated.social/services/ai-marketing-strategy/#service",
    url: "https://integrated.social/services/ai-marketing-strategy/",
  },
  {
    name: "PPC and Performance Max Agency London",
    id: "https://integrated.social/services/ppc-performance-max/#service",
    url: "https://integrated.social/services/ppc-performance-max/",
  },
  {
    name: "AI Governance, Safety and Regulatory Compliance for B2B",
    id: "https://integrated.social/services/ai-governance/#service",
    url: "https://integrated.social/services/ai-governance/",
  },
  {
    name: "AI marketing for ecommerce brands",
    id: "https://integrated.social/services/ai-marketing-agency-for-ecommerce/#service",
    url: "https://integrated.social/services/ai-marketing-agency-for-ecommerce/",
  },
] as const;

const organizationEntity = () => ({
  "@type": ["Organization", "ProfessionalService"],
  "@id": SITE_ENTITY_IDS.organization,
  name: "Integrated.Social",
  legalName: "Integrated Social Ltd",
  url: baseUrl,
  logo: {
    "@type": "ImageObject",
    url: "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
    width: 457,
    height: 457,
  },
  description: "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
  foundingDate: "2014",
  address: { "@type": "PostalAddress", addressLocality: "London", postalCode: "SW15 4LE", addressCountry: "GB" },
  areaServed: [
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "United Arab Emirates" },
  ],
  contactPoint: { "@type": "ContactPoint", contactType: "sales", email: "info@integrated.social", telephone: "+447717804077", areaServed: ["GB", "US", "AE"], availableLanguage: ["en-GB", "ar"] },
  knowsAbout: ["B2B AI Marketing", "B2B SaaS Marketing", "Agentic AI Lead Generation", "AI Content Operations", "Enterprise Technical SEO", "AI Visibility Measurement", "Answer Engine Optimisation (AEO)", "Generative Engine Optimisation (GEO)", "LLM Mention Optimisation (LLMO)", "AI Citation Readiness", "Google AI Mode Optimisation", "PPC and Performance Max", "Account-Based Marketing", "AI Governance for Marketing", "Conversion Rate Optimisation", "B2C and DTC Marketing", "Local AI search marketing", "Google Business Profile optimisation", "Local SEO for independent businesses"],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    "@id": "https://integrated.social/#core-services",
    name: "Integrated.Social core services",
    itemListElement: homepageCoreServiceOffers.map((service) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        "@id": service.id,
        name: service.name,
        url: service.url,
      },
    })),
  },
  founder: { "@id": SITE_ENTITY_IDS.founder },
  sameAs: ["https://www.linkedin.com/company/integrated-social-ltd"],
});

const founderEntity = () => ({
  "@type": "Person",
  "@id": SITE_ENTITY_IDS.founder,
  name: "Modi Elnadi",
  jobTitle: "Founder and Director of B2B AI Growth Marketing",
  description: "Modi Elnadi is the founder of Integrated.Social, a UK AI marketing agency focused on accountable AI search, paid media and growth strategy.",
  url: "https://integrated.social/modi-elnadi/",
  image: "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
  worksFor: { "@id": SITE_ENTITY_IDS.organization },
  sameAs: ["https://www.linkedin.com/in/modielnadi/", "https://x.com/MoElnadi"],
});

const websiteEntity = () => ({
  "@type": "WebSite",
  "@id": SITE_ENTITY_IDS.website,
  url: baseUrl,
  name: "Integrated.Social",
  publisher: { "@id": SITE_ENTITY_IDS.organization },
  inLanguage: "en-GB",
});

/**
 * Full, consistent root entities for a service-page graph. This prevents a
 * page-level Service/WebPage reference from dangling when parsed independently.
 * Fields originate here and are reused by the homepage graph below.
 */
export function getServiceSchemaSupportingEntities() {
  return [organizationEntity(), founderEntity(), websiteEntity()];
}

export type HomepageFaq = {
  q: string;
  a: string;
  link?: { href: string; label: string };
};

export const HOMEPAGE_FAQS: readonly HomepageFaq[] = [
  {
    q: "What does a UK AI marketing agency do?",
    a: "It connects the same buyer, evidence and commercial message across AI search, paid media and agentic workflows. The work is designed around an agreed business question, not a tool list.",
  },
  {
    q: "How do agents and people share the work?",
    a: "Agents may support research and early drafts, but a named person remains responsible for the claim, evidence, brand judgement and decision to publish or launch.",
  },
  {
    q: "Where does AEO sit if I already have SEO?",
    a: "AEO belongs within one search-visibility programme. SEO provides the technical and content foundation; answer-engine work makes the buyer question, evidence and entity context easier to understand.",
    link: { href: "/services/seo-aeo-geo/", label: "Explore SEO, AEO and GEO" },
  },
  {
    q: "How should a team measure AI visibility?",
    a: AI_VISIBILITY_MEASUREMENT_METHOD,
    link: { href: "/blog/ai-visibility-benchmark-2026-citation-rates-share-of-voice/", label: "Read the benchmark method" },
  },
  {
    q: "What is the right first step?",
    a: "Begin with the free AI Visibility Score for a high-level baseline. A paid AI Visibility Audit is a separate senior-led diagnostic when a team needs a scoped roadmap and walkthrough.",
  },
] as const;

/**
 * The homepage is the sole full definition of the site's root Organization,
 * WebSite, WebPage, and founder entities. Other pages reference these stable
 * IDs rather than re-declaring conflicting legal names, logos, or founder URLs.
 */
export function getHomepageEntityGraph() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      organizationEntity(),
      founderEntity(),
      {
        "@type": "WebSite",
        "@id": SITE_ENTITY_IDS.website,
        url: baseUrl,
        name: "Integrated.Social",
        publisher: { "@id": SITE_ENTITY_IDS.organization },
        inLanguage: "en-GB",
      },
      {
        "@type": "WebPage",
        "@id": SITE_ENTITY_IDS.homepage,
        url: baseUrl,
        name: "AI Marketing Agency UK | AEO, GEO & Agentic AI",
        description: "London AI Marketing Growth Agency for B2B, B2C and ecommerce brands. One accountable plan across AEO, GEO, paid media and agentic lead gen.",
        isPartOf: { "@id": SITE_ENTITY_IDS.website },
        about: { "@id": SITE_ENTITY_IDS.organization },
        mainEntity: { "@id": "https://integrated.social/#faq" },
        inLanguage: "en-GB",
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", "#entity-declaration", "#hero-services-heading", "#faq-teaser-heading"],
        },
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
          ],
        },
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/#faq",
        mainEntityOfPage: { "@id": SITE_ENTITY_IDS.homepage },
        mainEntity: HOMEPAGE_FAQS.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
      {
        "@type": "SiteNavigationElement",
        "@id": "https://integrated.social/#navigation",
        name: "Main navigation",
        hasPart: [
          { "@type": "WebPage", name: "Services", url: "https://integrated.social/services/" },
          { "@type": "WebPage", name: "Free AI Visibility Score", url: "https://integrated.social/free-ai-growth-audit/" },
          { "@type": "WebPage", name: "Blog", url: "https://integrated.social/blog/" },
          { "@type": "WebPage", name: "About Integrated.Social", url: "https://integrated.social/about/" },
          { "@type": "WebPage", name: "Contact", url: "https://integrated.social/contact/" },
        ],
      },

    ],
  };
}

export const organizationReference = { "@id": SITE_ENTITY_IDS.organization };
export const founderReference = { "@id": SITE_ENTITY_IDS.founder };
